import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { getAvailableCampaigns, addCampaignsToAffiliate } from '../../../influencer/services/affiliateService';

const AddAffiliateCampaigns = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAvailableCampaigns();
  }, [userId]);

  const fetchAvailableCampaigns = async () => {
    try {
      setLoading(true);
      const response = await getAvailableCampaigns(userId);
      if (response.success) {
        setCampaigns(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch campaigns');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error(error.message || 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignSelect = (campaignId) => {
    setSelectedCampaigns(prev => {
      if (prev.includes(campaignId)) {
        return prev.filter(id => id !== campaignId);
      }
      return [...prev, campaignId];
    });
  };

  const handleAddCampaigns = async () => {
    if (selectedCampaigns.length === 0) {
      toast.warning('Please select at least one campaign');
      return;
    }

    try {
      const response = await addCampaignsToAffiliate(userId, selectedCampaigns);
      if (response.success) {
        toast.success('Campaigns added successfully');
        navigate('/company/dashboard/affiliates/add');
      } else {
        toast.error(response.message || 'Failed to add campaigns');
      }
    } catch (error) {
      console.error('Error adding campaigns:', error);
      toast.error(error.message || 'Failed to add campaigns');
    }
  };

  const getCommissionDisplay = (commission) => {
    if (commission.model === 'fixed') {
      return `${commission.currency.symbol}${commission.amount}`;
    } else if (commission.percentage) {
      return `${commission.percentage}%`;
    } else if (commission.cpe) {
      return `CPE: ${commission.currency.symbol}${commission.cpe}`;
    } else if (commission.cpm) {
      return `CPM: ${commission.currency.symbol}${commission.cpm}`;
    }
    return 'N/A';
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col min-h-full bg-gray-100">
      <div className="flex-1 p-2 md:p-4 lg:p-6">
        <div className="bg-white shadow-md rounded-lg p-4 lg:p-6 mb-10 lg:mb-0">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/company/dashboard/affiliates/add')}
                className="text-gray-600 hover:text-gray-900"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <h2 className="text-2xl font-bold">Add Campaigns</h2>
            </div>
            <button
              onClick={handleAddCampaigns}
              disabled={selectedCampaigns.length === 0}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                selectedCampaigns.length === 0
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
            >
              <FaPlus />
              Add Selected Campaigns
            </button>
          </div>

          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Select
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Brand
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No campaigns found
                    </td>
                  </tr>
                ) : (
                  filteredCampaigns.map((campaign) => (
                    <tr key={campaign._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedCampaigns.includes(campaign._id)}
                          onChange={() => handleCampaignSelect(campaign._id)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {campaign.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {campaign.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getCommissionDisplay(campaign.commission)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {campaign.brandName}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAffiliateCampaigns; 